import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { StateType } from 'src/redux/reducers';
import {
  REQUEST_UPDATE_MATCH_CONTROL_CONTESTANT_MEMBERS,
  REQUEST_UPDATE_MATCH_CONTROL_LINE_UP_PRESET,
} from 'src/redux/reducers/matchControls';
import { NOTIFY } from 'src/redux/reducers/notification';
import { Contestant } from 'src/types/matchControl/contestant';

import { MatchControlButton, MatchControlIcon } from '../../components';
import LineUpDialog from '../../components/LineUp/LineUpDialog';
import styles from '../../MatchControl.module.scss';

import { DEFAULT_LINE_UP_PRESET, LineUpPreset, isLineUpPreset } from './memberColumns';

type LineUpButtonProps = {
  contestant: Contestant;
};

type LineUpProps = {
  home: Contestant;
  away: Contestant;
};

export const LineUpButton = ({ contestant }: LineUpButtonProps) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);

  // The column preset is stored on the match control, so it applies to the whole rundown.
  const matchControl = useSelector((state: StateType) => state.matchControls.matchControl.data);
  const configuredPreset = matchControl?.lineUpPreset;
  const preset = isLineUpPreset(configuredPreset) ? configuredPreset : DEFAULT_LINE_UP_PRESET;

  const hasLineUp = contestant.members && contestant.members.length > 0;

  const handleSubmitLineUp = (members: Contestant['members'], submittedPreset: LineUpPreset) => {
    if (submittedPreset === preset) {
      dispatch({
        type: REQUEST_UPDATE_MATCH_CONTROL_CONTESTANT_MEMBERS,
        payload: {
          contestantId: contestant.id,
          members,
        },
      });

      setOpen(false);
      return;
    }

    // Saving the members alone would drop the preset change without a trace, so
    // keep the dialog open with the entered rows intact instead.
    if (!matchControl?.id) {
      dispatch({
        type: NOTIFY,
        payload: {
          variant: 'error',
          title: 'Line-up not saved',
          description: 'The match control is unavailable, so the columns could not be changed.',
        },
      });
      return;
    }

    // The preset applies to the whole rundown, so the other line-ups no longer
    // match the columns and are emptied. One action keeps the writes ordered.
    dispatch({
      type: REQUEST_UPDATE_MATCH_CONTROL_LINE_UP_PRESET,
      payload: {
        matchControlId: matchControl.id,
        preset: submittedPreset,
        lineUps: (matchControl.contestants || []).map(({ id }) => ({
          contestantId: id,
          members: id === contestant.id ? members : [],
        })),
      },
    });

    setOpen(false);
  };

  return (
    <>
      <MatchControlButton onClick={() => setOpen(true)} className="gap-2" variant="icon">
        <MatchControlIcon iconProps={{ icon: hasLineUp ? 'pencil' : 'plus' }} />
        {hasLineUp ? 'Edit Line-up' : 'Add Line-up'}
      </MatchControlButton>
      <LineUpDialog
        open={open}
        setOpen={setOpen}
        contestant={contestant}
        preset={preset}
        onSubmit={handleSubmitLineUp}
      />
    </>
  );
};

export const LineUp = ({ home, away }: LineUpProps) => (
  <div className={styles.ItemWrapper}>
    <div className={styles.CurrentCardWrapper}>
      {[home, away].map((contestant) => (
        <div key={contestant.id} className={styles.CardWrapper}>
          <LineUpButton contestant={contestant} />
        </div>
      ))}
    </div>
  </div>
);
